/**
 * Janus lane MV1 · G0 — the shared SOLID-MATERIAL rule (plans/janus-lane-mv1-teleport-burrow-squeeze-handoff-2026-09-27.md,
 * design: plans/janus-m4b-teleport-m3d-burrow-plan-2026-09-24.md §1, owner rulings:
 * plans/burrow-and-blind-teleport-solid-rules-2026-09-24.md). PHP twin: includes/functions/janus/encounter/solid_material.php.
 * Parity: tools/test/janus-solid-material-php-ts-parity.mjs. Both sides must agree to the cell — change them together.
 *
 *  solidAt(ctx, x, y, z)            what fills a point: none | earth | stone | worked | adamantine | magical
 *  burrowTier({...})                how hard a material a creature can burrow through
 *  canBurrow(tier, material)        earth ≤ stone ≤ worked; adamantine and magical never
 *  bodyFreeAt(ctx, bodies, …)       a standing body's clearance (a room floor under it, no slab / ribbon / body in it)
 *  teleportDisplacement(ctx, …)     the landing a blind teleport actually reaches (ruling 5) + the feet displaced
 *
 * Geometry is feet; x/y are the floor plane, z is elevation (feet / contact plane).
 */
import { parsePaintedWallSectionSnapshot } from './painted-wall-section-state.js';
import { wallIntersectsCylinder, type PaintedWallGeometry } from './painted-wall-geometry.js';

export type SolidMaterial = 'none' | 'earth' | 'stone' | 'worked' | 'adamantine' | 'magical';
export type BurrowTier = 'none' | 'earth' | 'stone' | 'worked';
/** Overlap precedence: an embedded wall inside a box's air is still a wall. */
export const SOLID_RANK: Readonly<Record<SolidMaterial, number>> = { none: 0, earth: 1, stone: 2, worked: 3, adamantine: 4, magical: 5 };
const TIER_RANK: Readonly<Record<BurrowTier, number>> = { none: 0, earth: 1, stone: 2, worked: 3 };

/** DAFT3D S14 wall items: the row's item IS its material (ft3d_scene_wall_rows_inc.php). */
export const WALL_RUN_STONE_ITEM_ID = '82ED7687-84C5-A22C-F190-7EFC2BE98387';
export const WALL_RUN_TIMBER_ITEM_ID = '360C49A7-F61F-DC8B-DD02-DCC5C30A42C1';
export const WALL_RUN_ADAMANTINE_ITEM_ID = '8D2FE42D-EA49-8F25-CAED-201BBE53730A';
/** WALL_RUN_THICKNESS_FT (ft3d-creation-core.js): a room-generated run's slab. A scene wall carries its own thickness. */
export const WALL_RUN_THICKNESS_FT = 0.5;
/** Nothing free within this many feet of the landing: the teleport fails (ruling 5 default). */
export const TELEPORT_DISPLACEMENT_CUTOFF_FT = 60;
export const EARTH_GLIDE_ITEM_ID = 'FB1E8638-0087-E11E-11BE-10A395D788B5';
export const BURROWS_WORKED_STONE_LAYER = 'burrows_worked_stone';

export interface SolidBox { id: string; x: { min: number; max: number }; y: { min: number; max: number }; floor: number; ceiling: number;
  /** An open (outdoor) box has no walls and no roof: its air runs up forever and a body only needs its centre inside. */
  open: boolean; ground: 'earth' | 'stone' | 'worked' }
export interface SolidSlab { id: string; axis: 'x' | 'y'; at: number; half: number; span: { min: number; max: number };
  z: { min: number; max: number }; material: 'worked' | 'adamantine'; openings: Array<{ min: number; max: number; zmin: number; zmax: number }> }
export interface SolidContext { boxes: SolidBox[]; slabs: SolidSlab[]; ribbons: PaintedWallGeometry[] }
/** Another standing body (a creature) a landing must not overlap. */
export interface SolidBody { x: number; y: number; z: number; radius: number; height: number }
export interface Point3 { x: number; y: number; z: number }

type Row = Record<string, unknown>;
const EPS = 1e-6;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
/** PHP is_numeric() twin (Codex G0 review): a number or a non-empty numeric string - never null / '' / false read as 0. */
const NUMERIC = /^\s*[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?\s*$/;
const num = (value: unknown): number => {
  const v = scalar(value);
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  if (typeof v === 'string' && NUMERIC.test(v)) { const n = Number(v); return Number.isFinite(n) ? n : NaN; }
  return NaN;
};
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();

/** A box's ground: `creation.solidMaterial` when authored, else the preset (a cave is stone, everything else earth). */
export function boxGround(box: Row): SolidBox['ground'] {
  const creation = record(box.creation) ?? {};
  const authored = String(creation.solidMaterial ?? '');
  if (authored === 'earth' || authored === 'stone' || authored === 'worked') return authored;
  return /cave/i.test(String(creation.presetId ?? '')) ? 'stone' : 'earth';
}

/** Build the context from its three sources: the shape's boxes, live wall-run inventory rows, painted walls. */
export function solidContext(shape: unknown, wallRows: ReadonlyArray<{ itemId: string; generation: unknown }>, paintedWalls: readonly PaintedWallGeometry[]): SolidContext {
  const s = record(shape) ?? {};
  const boxes: SolidBox[] = [];
  for (const raw of Array.isArray(s.boxes) ? s.boxes : []) {
    const box = record(raw);
    const bx = record(box?.x), by = record(box?.y);
    if (!box || !bx || !by) continue;
    const values = [num(bx.min), num(bx.max), num(by.min), num(by.max), num(box.floor), num(box.ceiling)];
    if (!values.every(Number.isFinite)) continue;
    const creation = record(box.creation) ?? {};
    boxes.push({ id: String(box.id ?? ''), x: { min: values[0]!, max: values[1]! }, y: { min: values[2]!, max: values[3]! },
      floor: values[4]!, ceiling: values[5]!, open: box.interior === 'open' || creation.enclosure === 'none', ground: boxGround(box) });
  }
  const sceneWalls = new Map<string, Row>();
  for (const raw of Array.isArray(s.sceneWalls) ? s.sceneWalls : []) {
    const wall = record(raw);
    if (wall && typeof wall.id === 'string') sceneWalls.set(wall.id, wall);
  }
  const slabs: SolidSlab[] = [];
  for (const row of wallRows) {
    let generation = row.generation;
    if (typeof generation === 'string') { try { generation = JSON.parse(generation) as unknown; } catch { continue; } }
    const run = record(record(generation)?.wallRun);
    if (!run || (run.axis !== 'x' && run.axis !== 'y')) continue;
    const span = record(run.span), z = record(run.z);
    const values = [num(run.at), num(span?.min), num(span?.max), num(z?.min), num(z?.max)];
    if (!values.every(Number.isFinite) || !(values[2]! > values[1]!) || !(values[4]! > values[3]!)) continue;
    const scene = typeof run.sceneWallId === 'string' ? sceneWalls.get(run.sceneWallId) ?? null : null;
    const thickness = scene ? num(scene.thicknessFt) : WALL_RUN_THICKNESS_FT;
    const openings: SolidSlab['openings'] = [];
    for (const rawOpening of scene && Array.isArray(scene.openings) ? scene.openings : []) {
      const opening = record(rawOpening), os = record(opening?.span), oz = record(opening?.z);
      const o = [num(os?.min), num(os?.max), oz ? num(oz.min) : values[3]!, oz ? num(oz.max) : values[4]!];
      if (o.every(Number.isFinite) && o[1]! > o[0]!) openings.push({ min: o[0]!, max: o[1]!, zmin: o[2]!, zmax: o[3]! });
    }
    slabs.push({ id: String(run.key ?? ''), axis: run.axis, at: values[0]!, half: Math.max(0.05, Number.isFinite(thickness) ? thickness : WALL_RUN_THICKNESS_FT) / 2,
      span: { min: values[1]!, max: values[2]! }, z: { min: values[3]!, max: values[4]! },
      material: upper(row.itemId) === WALL_RUN_ADAMANTINE_ITEM_ID ? 'adamantine' : 'worked', openings });
  }
  return { boxes, slabs, ribbons: [...paintedWalls] };
}

/** The encounter adapter: shape, live wall-run rows, and every painted-wall zone anchor (ft_solid_context_from_encounter twin). */
export function solidContextFromEncounter(encounter: Row, statuses: (row: Row) => Row[]): SolidContext {
  const encounters = record(encounter.encounters) ?? {};
  let shape = encounters.encounter_shape ?? encounter.encounter_shape;
  if (typeof shape === 'string') { try { shape = JSON.parse(shape) as unknown; } catch { shape = null; } }
  const inventories = (Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr : []).map(record).filter((r): r is Row => r !== null);
  const field = (row: Row, key: string): unknown => scalar(record(row.inventory)?.[key] ?? row[key]);
  const wallRows: Array<{ itemId: string; generation: unknown }> = [];
  for (const row of inventories) {
    if (num(field(row, 'inventory_deleted')) === 1) continue;
    const generation = field(row, 'inventory_generation');
    if (generation === undefined || generation === null || generation === '') continue;
    wallRows.push({ itemId: String(field(row, 'inventory_item_id') ?? ''), generation });
  }
  const ribbons: PaintedWallGeometry[] = [];
  for (const row of [encounter, ...inventories, ...(Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []).map(record).filter((r): r is Row => r !== null)]) {
    for (const status of statuses(row)) {
      if (Number(status.status_aoe_source ?? 0) !== 1) continue;
      const snapshot = parsePaintedWallSectionSnapshot(status.status_aoe_geometry);
      if (snapshot) ribbons.push(snapshot.geometry);
    }
  }
  return solidContext(shape, wallRows, ribbons);
}

/** strcmp order (PHP twin), never localeCompare. */
const byId = (a: { id: string }, b: { id: string }): number => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
const inRange = (v: number, min: number, max: number): boolean => v >= min - EPS && v <= max + EPS;

/** The slab's solid pieces along its span at a height: its openings cut out. */
function slabPieces(slab: SolidSlab, zLow: number, zHigh: number): Array<[number, number]> {
  let pieces: Array<[number, number]> = [[slab.span.min, slab.span.max]];
  for (const opening of slab.openings) {
    if (zLow < opening.zmin - EPS || zHigh > opening.zmax + EPS) continue; // the opening must span the whole height asked about
    const next: Array<[number, number]> = [];
    for (const [lo, hi] of pieces) {
      if (opening.max <= lo || opening.min >= hi) { next.push([lo, hi]); continue; }
      if (opening.min - lo > EPS) next.push([lo, Math.min(hi, opening.min)]);
      if (hi - opening.max > EPS) next.push([Math.max(lo, opening.max), hi]);
    }
    pieces = next;
  }
  return pieces;
}

/** What fills the point (x, y, z). Painted walls are magical; wall-run slabs are their row's material; a box's air is
 *  none; everything else is ground — the box whose floor lies above the point (or whose closed roof lies below it) owns it,
 *  and ground outside every box is earth. */
export function solidAt(ctx: SolidContext, x: number, y: number, z: number): SolidMaterial {
  for (const ribbon of ctx.ribbons) if (wallIntersectsCylinder(ribbon, { x, y, z }, 0, 0)) return 'magical';
  let best: SolidMaterial = 'none';
  for (const slab of ctx.slabs) {
    const normal = slab.axis === 'x' ? x : y, along = slab.axis === 'x' ? y : x;
    if (Math.abs(normal - slab.at) > slab.half + EPS || !inRange(z, slab.z.min, slab.z.max)) continue;
    if (!slabPieces(slab, z, z).some(([lo, hi]) => inRange(along, lo, hi))) continue;
    if (SOLID_RANK[slab.material] > SOLID_RANK[best]) best = slab.material;
  }
  if (best !== 'none') return best;
  const over = ctx.boxes.filter(box => inRange(x, box.x.min, box.x.max) && inRange(y, box.y.min, box.y.max));
  if (over.some(box => z >= box.floor - EPS && (box.open || z < box.ceiling - EPS))) return 'none';
  // Below a floor: the NEAREST floor above owns the ground (a storey's slab belongs to the room standing on it).
  const above = over.filter(box => box.floor > z + EPS).sort((a, b) => a.floor - b.floor || byId(a, b));
  if (above.length) return above[0]!.ground;
  // Above a closed roof with nothing standing on it: the rock the room was cut into.
  const below = over.filter(box => !box.open && box.ceiling <= z + EPS).sort((a, b) => b.ceiling - a.ceiling || byId(a, b));
  if (below.length) return below[0]!.ground;
  return 'earth';
}

/** Burrow tier: a burrowing speed digs earth; Earth Glide adds natural stone; the burrows_worked_stone flag adds masonry. */
export function burrowTier(input: { burrowSpeedFt: number; earthGlide: boolean; worksStone: boolean }): BurrowTier {
  if (!(input.burrowSpeedFt > 0)) return 'none';
  return input.worksStone ? 'worked' : input.earthGlide ? 'stone' : 'earth';
}
export function canBurrow(tier: BurrowTier, material: SolidMaterial): boolean {
  if (material === 'none') return true;
  if (material === 'adamantine' || material === 'magical') return false;
  return TIER_RANK[tier] >= TIER_RANK[material];
}

/** Does a body (vertical cylinder, feet at z) fit here? ONE box's air must hold it (a closed box keeps its radius from the
 *  walls and its height under the roof; an open box only needs its centre over it), and it may overlap no wall slab, painted
 *  ribbon or other body. `standing` (the default, every displacement candidate) also puts its feet on that box's floor; a
 *  teleport LANDING is judged with standing=false - clear air is neither solid nor occupied, so a mid-air landing is never
 *  displaced (Codex G0 review; the fall is the legacy commit's). The deterministic free-position rule both sides share (ruling 5). */
export function bodyFreeAt(ctx: SolidContext, bodies: readonly SolidBody[], x: number, y: number, z: number, radius: number, height: number,
  standing = true): boolean {
  const holder = ctx.boxes.some(box => (standing ? Math.abs(z - box.floor) <= 0.01 : z >= box.floor - 0.01)
    && (box.open ? inRange(x, box.x.min, box.x.max) && inRange(y, box.y.min, box.y.max)
      : x - radius >= box.x.min - EPS && x + radius <= box.x.max + EPS && y - radius >= box.y.min - EPS && y + radius <= box.y.max + EPS
        && z + height <= box.ceiling + EPS));
  if (!holder) return false;
  for (const slab of ctx.slabs) {
    if (!(z < slab.z.max - EPS && z + height > slab.z.min + EPS)) continue;
    const normal = slab.axis === 'x' ? x : y, along = slab.axis === 'x' ? y : x;
    const gap = Math.max(0, Math.abs(normal - slab.at) - slab.half);
    if (gap >= radius - EPS) continue;
    // Openings are judged over the part of the body the slab actually spans (a 5 ft body in a 3 ft wall's doorway fits).
    for (const [lo, hi] of slabPieces(slab, Math.max(z, slab.z.min), Math.min(z + height, slab.z.max))) {
      if (Math.hypot(gap, Math.max(0, lo - along, along - hi)) < radius - EPS) return false;
    }
  }
  for (const ribbon of ctx.ribbons) if (wallIntersectsCylinder(ribbon, { x, y, z }, radius, height)) return false;
  for (const body of bodies) {
    if (!(z < body.z + body.height - EPS && z + height > body.z + EPS)) continue;
    if (Math.hypot(x - body.x, y - body.y) < radius + body.radius - EPS) return false;
  }
  return true;
}

export interface TeleportDisplacement { point: Point3; displacedFt: number; blocked: boolean; dice: number }
const key6 = (value: number): number => Math.round(value * 1e6);
/** 2d10 force per 10 ft displaced, any displacement rolls at least once (plan §1 default). */
export function displacementDice(displacedFt: number): number {
  return displacedFt > 1e-9 ? Math.max(1, Math.ceil((displacedFt - 1e-9) / 10)) : 0;
}

/**
 * Where a blind teleport lands (ruling 5). The landing itself when a body fits there; else the nearest free spot by
 * straight 3D distance on the 1 ft lattice of every box floor, plus the landing's projection into each box, within 60 ft
 * (squared distances compared at 1e-6), ties
 * toward the caster's origin, then (x, y, z) ascending — PHP and TS pick the same cell independently. Nothing free →
 * blocked (the teleport fails, no damage).
 */
export function teleportDisplacement(ctx: SolidContext, bodies: readonly SolidBody[], origin: Point3, landing: Point3,
  radius: number, height: number): TeleportDisplacement {
  if (bodyFreeAt(ctx, bodies, landing.x, landing.y, landing.z, radius, height, false)) return { point: { ...landing }, displacedFt: 0, blocked: false, dice: 0 };
  const cutoff = TELEPORT_DISPLACEMENT_CUTOFF_FT;
  const candidates: Array<{ x: number; y: number; z: number; d: number; o: number }> = [];
  for (const box of ctx.boxes) {
    const z = box.floor;
    const dz = z - landing.z;
    if (Math.abs(dz) > cutoff + EPS) continue;
    const inset = box.open ? 0 : radius;
    const x0 = Math.ceil(Math.max(box.x.min + inset, landing.x - cutoff) - EPS), x1 = Math.floor(Math.min(box.x.max - inset, landing.x + cutoff) + EPS);
    const y0 = Math.ceil(Math.max(box.y.min + inset, landing.y - cutoff) - EPS), y1 = Math.floor(Math.min(box.y.max - inset, landing.y + cutoff) + EPS);
    const push = (x: number, y: number): void => {
      const ex = x - landing.x, ey = y - landing.y, ox = x - origin.x, oy = y - origin.y, oz = z - origin.z;
      const d2 = ex * ex + ey * ey + dz * dz;
      if (d2 > cutoff * cutoff + EPS) return;
      candidates.push({ x, y, z, d: key6(d2), o: key6(ox * ox + oy * oy + oz * oz) });
    };
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) push(x, y);
    // The landing projected into the box's standable rectangle (Codex G0 review): a room narrower than the lattice's reach
    // (a 5 ft corridor for a 5 ft body has only x = 2.5) still offers its free spot.
    const lowX = box.x.min + inset, highX = box.x.max - inset, lowY = box.y.min + inset, highY = box.y.max - inset;
    if (lowX <= highX + EPS && lowY <= highY + EPS) push(Math.min(Math.max(landing.x, lowX), highX), Math.min(Math.max(landing.y, lowY), highY));
  }
  candidates.sort((a, b) => a.d - b.d || a.o - b.o || a.x - b.x || a.y - b.y || a.z - b.z);
  let last: { x: number; y: number; z: number } | null = null;
  for (const c of candidates) {
    if (last && last.x === c.x && last.y === c.y && last.z === c.z) continue; // two boxes sharing a floor cell
    last = c;
    if (!bodyFreeAt(ctx, bodies, c.x, c.y, c.z, radius, height)) continue;
    // Damage from the ACTUAL distance (Codex G0 review); the 1e-6 key only orders candidates, the 3 dp only displays.
    const ex = c.x - landing.x, ey = c.y - landing.y, ez = c.z - landing.z;
    const exact = Math.sqrt(ex * ex + ey * ey + ez * ez);
    return { point: { x: c.x, y: c.y, z: c.z }, displacedFt: Math.round(exact * 1000) / 1000, blocked: false, dice: displacementDice(exact) };
  }
  return { point: { ...landing }, displacedFt: 0, blocked: true, dice: 0 };
}
